<template>
  <layout-default>
    <div class="container vendor-index-page">
      <div
        v-if="loading"
        class="loader-wrapper d-flex justify-content-center align-items-center"
      >
        <div class="loader"></div>
      </div>

      <div v-else-if="vendors.length" class="vendor-index__content w-100">
        <div class="vendor-index__header text-center mb-5">
          <h1 class="vendor-index__title">Master Craftsmen</h1>
          <p class="text-secondary">
            Discover artisans and makers. Select a vendor to explore their work.
          </p>
        </div>
        <div class="row vendor-grid">
          <div
            v-for="vendor in vendors"
            :key="vendor.id"
            class="col-12 col-md-6 col-lg-4 mb-4"
          >
            <LinkWrapper
              class="text-decoration-none"
              :location="vendorLink(vendor)"
              :removeDecorations="true"
            >
              <article class="vendor-card h-100 d-flex flex-column">
                <div class="vendor-card__image">
                  <img
                    v-if="vendorImageSrc(vendor)"
                    :src="vendorImageSrc(vendor)"
                    :alt="vendor.name"
                  />
                  <div v-else class="vendor-card__placeholder">
                    No image available
                  </div>
                </div>
                <div class="vendor-card__body flex-grow-1 d-flex flex-column">
                  <h2 class="vendor-card__name">{{ vendor.name }}</h2>
                  <p v-if="vendor.description" class="vendor-card__description">
                    {{ truncateDescription(vendor.description) }}
                  </p>
                  <p class="vendor-card__meta mb-0 mt-auto">
                    {{ vendor.totalProducts ?? 0 }} product<span
                      v-if="(vendor.totalProducts ?? 0) !== 1"
                      >s</span
                    >
                  </p>
                </div>
              </article>
            </LinkWrapper>
          </div>
        </div>
      </div>

      <div v-else class="vendor-index__empty text-center w-100">
        <h1 class="vendor-index__title">Master Craftsmen</h1>
        <p class="text-secondary">
          No vendors are available right now. Please check back later.
        </p>
        <ButtonFilled
          :hasRoundedCorners="true"
          text="Go back home"
          @onClick="goHome"
        />
      </div>
    </div>
  </layout-default>
</template>

<script>
import { onMounted, ref, getCurrentInstance } from "vue";
import LayoutDefault from "../../components/organisms/LayoutDefault/LayoutDefault.vue";
import ButtonFilled from "../../components/atoms/Button/ButtonFilled.vue";
import { fetchVendors } from "../../composables/manageVendors";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import LinkWrapper from "../../components/atoms/LinkWrapper/LinkWrapper.vue";

export default {
  name: "VendorIndex",
  components: {
    LayoutDefault,
    ButtonFilled,
    LinkWrapper,
  },
  setup() {
    const loading = ref(true);
    const vendors = ref([]);
    const router = useRouter();
    const store = useStore();
    const globals =
      getCurrentInstance()?.appContext.config.globalProperties ?? {};

    onMounted(async () => {
      try {
        document.title = "Master Craftsmen - viabrera";
        store.commit("setLocation", [{ title: "Master Craftsmen", location: "/vendors" }]);
        vendors.value = await fetchVendors();
      } finally {
        loading.value = false;
      }
    });

    const vendorLink = (vendor) => {
      if (!vendor) return "/vendors";
      return (
        globals.$buildVendorURL?.(vendor, vendor.id) ||
        `/vendors/${vendor.containerPath || vendor.slug || vendor.id}`
      );
    };

    const vendorImageSrc = (vendor) => {
      if (!vendor) return "";
      const src = vendor.gallery?.[0] || vendor.image;
      if (!src) return "";
      if (/^https?:\/\//i.test(src)) {
        return src;
      }
      return globals.$getFileSource ? globals.$getFileSource(src) : src;
    };

    const goHome = () => router.push({ name: "Home" });

    const truncateDescription = (text, limit = 140) => {
      if (!text) return "";
      if (text.length <= limit) return text;
      return `${text.slice(0, limit - 1).trim()}…`;
    };

    return {
      loading,
      vendors,
      vendorLink,
      vendorImageSrc,
      goHome,
      truncateDescription,
    };
  },
};
</script>

<style scoped>
.vendor-index-page {
  padding: 80px 0 120px;
  min-height: 420px;
}

.vendor-index__title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
}

.vendor-index__content {
  max-width: 1200px;
}

.vendor-index__empty {
  max-width: 560px;
  margin: 0 auto;
}

.vendor-grid {
  row-gap: 24px;
}

.vendor-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
  background: #ffffff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.vendor-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.18);
}

.vendor-card__image {
  position: relative;
  padding-top: 62%;
  background: #f7f7f7;
}

.vendor-card__image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.vendor-card__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
  font-size: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.vendor-card__body {
  padding: 24px;
}

.vendor-card__name {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1d1d1d;
}

.vendor-card__description {
  color: #5a5a5a;
  font-size: 15px;
  line-height: 1.6;
}

.vendor-card__meta {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #8c8c8c;
}

@media (max-width: 575.98px) {
  .vendor-index__title {
    font-size: 26px;
  }

  .vendor-card__body {
    padding: 20px;
  }
}
</style>

<style>
  .loader-wrapper {
    min-width: 100%;
    min-height: 200px;
  }
  .loader-wrapper .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
